'use client';

import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { SettingsQuestionnaireSection } from '@/components/settings-questionnaire-modal/SettingsQuestionnaireSections';
import {
    EditableSettingsTabId,
    SettingsTabId,
} from '@/components/settings-questionnaire-modal/settings-questionnaire.schema';
import { useSettingsQuestionnaireModalController } from '@/components/settings-questionnaire-modal/use-settings-questionnaire-modal-controller';
import { getConfiguredOnboardingAgreementLocales } from '@/config/public-client-config';
import { AUTH_TOKEN_PARAM } from '@/constants/auth';
import { useAuth } from '@/context/auth.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { UIPriority } from '@/enums/ui-priorities';
import { ONBOARDING_AGREEMENT_TEXTS, OnboardingAgreementLocale } from '@/shared/onboarding-agreement';
import {
    extractApiErrorMessageFromUnknown,
    extractApiValidationErrorMessageFromUnknown,
} from '@/shared/errors/api-error';
import Button from '@/ui/Button';
import { toast } from '@/ui/toast';
import clsx from 'clsx';
import { FC, UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type OnboardingStepId = EditableSettingsTabId | 'agreement';

type OnboardingStep = {
    id: OnboardingStepId;
    label: string;
};

const onboardingLogger = createClientLogger({
    scope: 'client-onboarding',
});

const ONBOARDING_STEPS: OnboardingStep[] = [
    { id: 'general', label: 'Общие' },
    { id: 'channels', label: 'Каналы' },
    { id: 'review-import', label: 'Импорт отзывов' },
    { id: 'ai-strategy', label: 'ИИ-стратегия' },
    { id: 'agreement', label: 'Оферта' },
];

const isEditableOnboardingStep = (stepId: OnboardingStepId): stepId is EditableSettingsTabId => stepId !== 'agreement';

const redirectToAuthenticatedCurrentUrl = (accessToken: string): void => {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set(AUTH_TOKEN_PARAM, accessToken);
    window.location.assign(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
};

const isScrolledToEnd = (element: HTMLElement): boolean =>
    element.scrollHeight <= element.clientHeight ||
    element.scrollTop + element.clientHeight >= element.scrollHeight - 4;

const getStepTitle = (step: OnboardingStep): string => {
    if (step.id === 'agreement') {
        return 'Оферта';
    }

    return step.label;
};

export const ClientOnboardingPage: FC = () => {
    const { accessToken, clientId } = useAuth();
    const { onboardingApi } = useMarketplaceApi();
    const controller = useSettingsQuestionnaireModalController({
        isShown: true,
        setIsShown: () => undefined,
    });
    const availableAgreementLocales = useMemo(() => getConfiguredOnboardingAgreementLocales(), []);
    const [stepIndex, setStepIndex] = useState(0);
    const [agreementLocale, setAgreementLocale] = useState<OnboardingAgreementLocale>(
        () => availableAgreementLocales[0] ?? 'ru',
    );
    const [hasScrolledAgreement, setHasScrolledAgreement] = useState(false);
    const [hasAcceptedAgreementTerms, setHasAcceptedAgreementTerms] = useState(false);
    const [isAcceptingAgreement, setIsAcceptingAgreement] = useState(false);
    const agreementScrollRef = useRef<HTMLDivElement | null>(null);
    const pageScrollRef = useRef<HTMLElement | null>(null);
    const currentStep = ONBOARDING_STEPS[stepIndex] ?? ONBOARDING_STEPS[0];
    const isAgreementStep = currentStep.id === 'agreement';
    const isBackAvailable = stepIndex > 0 && !isAcceptingAgreement;
    const isContinueDisabled = controller.isLoading || controller.isSaving || !controller.hasLoadedSettings;
    const isAcceptDisabled =
        isContinueDisabled || isAcceptingAgreement || !hasScrolledAgreement || !hasAcceptedAgreementTerms;
    const agreement = ONBOARDING_AGREEMENT_TEXTS[agreementLocale];
    const agreementVersion = agreement.version;
    const progressLabel = useMemo(() => `${stepIndex + 1}/${ONBOARDING_STEPS.length}`, [stepIndex]);

    const updateAgreementScrollState = useCallback(() => {
        const scrollElement = agreementScrollRef.current;
        if (!scrollElement) {
            setHasScrolledAgreement(false);
            return;
        }

        setHasScrolledAgreement(isScrolledToEnd(scrollElement));
    }, []);

    useEffect(() => {
        setHasScrolledAgreement(false);
        const schedule =
            typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame : window.setTimeout;
        schedule(updateAgreementScrollState);
    }, [agreementLocale, isAgreementStep, updateAgreementScrollState]);

    useEffect(() => {
        pageScrollRef.current?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
        window.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
    }, [stepIndex]);

    const goBack = useCallback(() => {
        setStepIndex((prevStepIndex) => Math.max(0, prevStepIndex - 1));
    }, []);

    const goNext = useCallback(async () => {
        if (!isEditableOnboardingStep(currentStep.id)) {
            return;
        }

        const saved = await controller.saveTab(currentStep.id as SettingsTabId, {
            closeAfterSave: false,
            successMessage: null,
        });

        if (saved) {
            setStepIndex((prevStepIndex) => Math.min(ONBOARDING_STEPS.length - 1, prevStepIndex + 1));
        }
    }, [controller, currentStep.id]);

    const acceptAgreement = useCallback(async () => {
        if (!hasScrolledAgreement || !hasAcceptedAgreementTerms || isAcceptingAgreement) {
            return;
        }

        setIsAcceptingAgreement(true);
        onboardingLogger.info('[client.onboarding.agreement-accept] Accepted agreement submission.', {
            clientId,
            locale: agreementLocale,
            agreementVersion,
        });

        try {
            const response = await onboardingApi.acceptAgreement(
                { locale: agreementLocale },
                { authToken: accessToken },
            );
            onboardingLogger.info('[client.onboarding.agreement-accept] Completed agreement submission.', {
                clientId,
                locale: agreementLocale,
                agreementVersion: response.data.agreementAcceptedOfferVersion,
                welcomePackageGranted: response.data.welcomePackageGranted,
                agreementAcceptedNow: response.data.agreementAcceptedNow,
            });
            toast.success('Оферта принята');
            redirectToAuthenticatedCurrentUrl(accessToken);
        } catch (error) {
            logClientError({
                logger: onboardingLogger,
                message: '[client.onboarding.agreement-accept] Failed to accept agreement.',
                error,
                context: {
                    clientId,
                    locale: agreementLocale,
                    agreementVersion,
                },
            });
            const errorMessage =
                extractApiValidationErrorMessageFromUnknown(error) ??
                extractApiErrorMessageFromUnknown(error) ??
                'Не удалось принять оферту';
            toast.error(errorMessage);
            setIsAcceptingAgreement(false);
        }
    }, [
        accessToken,
        agreementLocale,
        agreementVersion,
        clientId,
        hasAcceptedAgreementTerms,
        hasScrolledAgreement,
        isAcceptingAgreement,
        onboardingApi,
    ]);

    const handleAgreementScroll = (event: UIEvent<HTMLDivElement>) => {
        setHasScrolledAgreement(isScrolledToEnd(event.currentTarget));
    };

    const renderStepContent = () => {
        if (controller.isLoading || !controller.hasLoadedSettings) {
            return <div className="flex min-h-40 items-center justify-center text-sm text-slate-500">Загрузка...</div>;
        }

        if (isEditableOnboardingStep(currentStep.id)) {
            return (
                <SettingsQuestionnaireSection
                    activeTab={currentStep.id}
                    idPrefix={`onboarding-${currentStep.id}`}
                    formState={controller.formState}
                    questions={controller.questions}
                    completionProgress={controller.completionProgress}
                    onStoreNameChanged={controller.onStoreNameChanged}
                    onCompanyInfoLinkChanged={controller.onCompanyInfoLinkChanged}
                    onEmailChanged={controller.onEmailChanged}
                    onCommentsChanged={controller.onCommentsChanged}
                    onAnswerChanged={controller.onAnswerChanged}
                    onGeneralSettingChanged={controller.onGeneralSettingChanged}
                    onChannelChanged={controller.onChannelChanged}
                    onMarketplaceReviewImportLimitChanged={controller.onMarketplaceReviewImportLimitChanged}
                />
            );
        }

        return (
            <div className="space-y-3">
                <div className="flex flex-wrap gap-2" aria-label="Выбор языка оферты">
                    {availableAgreementLocales.map((locale) => {
                        const isSelected = agreementLocale === locale;

                        return (
                            <button
                                key={locale}
                                type="button"
                                className={clsx(
                                    'rounded-md border px-3 py-2 text-sm',
                                    isSelected
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-slate-200 bg-white text-slate-700',
                                )}
                                aria-pressed={isSelected}
                                onClick={() => setAgreementLocale(locale)}
                            >
                                {ONBOARDING_AGREEMENT_TEXTS[locale].label}
                            </button>
                        );
                    })}
                </div>
                <div
                    ref={agreementScrollRef}
                    className="rounded-lg border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-800 shadow-sm"
                    onScroll={handleAgreementScroll}
                    tabIndex={0}
                    aria-label={`Оферта, ${agreement.label}`}
                >
                    {agreement.paragraphs.map((paragraph, index) => (
                        <p key={`${agreementLocale}-${index}`} className="mb-3 whitespace-pre-line">
                            {paragraph}
                        </p>
                    ))}
                </div>
                <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm font-medium text-slate-900 shadow-sm">
                    <input
                        type="checkbox"
                        required
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={hasAcceptedAgreementTerms}
                        onChange={(event) => setHasAcceptedAgreementTerms(event.currentTarget.checked)}
                    />
                    <span>Согласен с условиями оферты</span>
                </label>
            </div>
        );
    };

    return (
        <main className="min-h-screen bg-slate-50">
            <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-3 py-4 sm:px-5">
                <header className="shrink-0 border-b border-slate-200 pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div className="text-xs font-medium uppercase text-slate-500">{progressLabel}</div>
                            <h1 className="mt-1 text-xl font-semibold text-slate-950">{getStepTitle(currentStep)}</h1>
                        </div>
                        <ol className="flex min-w-0 flex-wrap gap-2" aria-label="Шаги онбординга">
                            {ONBOARDING_STEPS.map((step, index) => {
                                const isCurrent = index === stepIndex;
                                const isCompleted = index < stepIndex;

                                return (
                                    <li
                                        key={step.id}
                                        className={clsx(
                                            'rounded-md border px-2.5 py-1.5 text-xs',
                                            isCurrent
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : isCompleted
                                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                                  : 'border-slate-200 bg-white text-slate-500',
                                        )}
                                    >
                                        {step.label}
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </header>

                <section ref={pageScrollRef} className="min-h-0 flex-1 overflow-y-auto py-4">
                    <div className="flex min-h-full flex-col gap-4">
                        <div className="flex-1">{renderStepContent()}</div>
                        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 pt-3">
                            <Button
                                type="button"
                                priorityType={UIPriority.SECONDARY}
                                className="max-w-36"
                                disabled={!isBackAvailable}
                                onClick={goBack}
                            >
                                Назад
                            </Button>
                            {isAgreementStep ? (
                                <Button
                                    type="button"
                                    priorityType={UIPriority.PRIMARY}
                                    className="max-w-52"
                                    disabled={isAcceptDisabled}
                                    onClick={acceptAgreement}
                                >
                                    {isAcceptingAgreement ? 'Принятие...' : 'Принять оферту'}
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    priorityType={UIPriority.PRIMARY}
                                    className="max-w-36"
                                    disabled={isContinueDisabled}
                                    onClick={goNext}
                                >
                                    {controller.isSaving ? 'Сохранение...' : 'Далее'}
                                </Button>
                            )}
                        </footer>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default ClientOnboardingPage;
